import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';

const MSG = 'KOLMY_EXTENSION_SAVE_TOKEN';

/** Best-effort name for this browser, so the device list is readable. */
function deviceLabel(): string {
  const ua = navigator.userAgent;
  const os = /Macintosh|Mac OS X/i.test(ua)
    ? 'Mac'
    : /Windows/i.test(ua)
      ? 'Windows'
      : /Linux/i.test(ua)
        ? 'Linux'
        : null;
  return os ? `Chrome on ${os}` : 'Chrome extension';
}

/**
 * Open from the kol.my Insight extension after signing in on the web app.
 *
 * Mints a long-lived, revocable device token and hands it to the extension via postMessage (the
 * content script relays it to the service worker). This replaced a 15-minute access JWT that the
 * extension had no way to refresh, so a connected extension stopped working a quarter of an hour
 * later. Devices are listed and revocable at /agency/extension-devices.
 */
export default function ExtensionConnectPage() {
  const { user, isAuthenticated, isLoading } = useAuthStore();
  const [status, setStatus] = useState<'idle' | 'working' | 'ok' | 'err'>('idle');
  const [message, setMessage] = useState('');
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated || !user) {
      setStatus('err');
      setMessage('Sign in to kol.my first, then return to this page.');
      return;
    }
    if (user.role !== 'AGENCY' && user.role !== 'ADMIN') {
      setStatus('err');
      setMessage('The Insight extension is for agency (or admin) accounts. Switch to an agency workspace user.');
      return;
    }

    let cancelled = false;
    (async () => {
      setStatus('working');
      setMessage('Connecting extension…');
      try {
        const rawBase = import.meta.env.VITE_API_URL as string | undefined;
        const apiBase = rawBase
          ? rawBase.endsWith('/api')
            ? rawBase.replace(/\/+$/, '')
            : `${rawBase.replace(/\/+$/, '')}/api`
          : `${window.location.origin.replace(/\/+$/, '')}/api`;

        const { data } = await api.post<{
          deviceToken: string;
          deviceId: string;
          tokenPrefix: string;
          label: string;
          expiresAt: string;
          agencyOrgId: string;
        }>('/auth/extension/devices', {
          label: deviceLabel(),
          extensionVersion: new URLSearchParams(window.location.search).get('v') || undefined,
        });
        if (cancelled) return;
        const deviceToken = data?.deviceToken;
        if (!deviceToken) {
          setStatus('err');
          setMessage('Could not issue a token. Try signing out and back in.');
          return;
        }
        window.postMessage(
          {
            type: MSG,
            deviceToken,
            // `accessToken` kept for one release so an extension build predating device tokens
            // still reads something it recognises from this page.
            accessToken: deviceToken,
            apiBaseUrl: apiBase,
            agencyOrgId: data.agencyOrgId,
            tokenPrefix: data.tokenPrefix,
          },
          window.location.origin
        );
        setExpiresAt(data.expiresAt || null);
        setStatus('ok');
        setMessage(
          'You are connected. Close this tab and open the extension on a creator profile. If nothing updates, reload the extension in chrome://extensions.'
        );
      } catch (e: unknown) {
        if (cancelled) return;
        const err = e as { response?: { data?: { error?: string } } };
        setStatus('err');
        setMessage(err.response?.data?.error || 'Connection failed. Try again or sign in again.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoading, isAuthenticated, user]);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex flex-col items-center justify-center p-6">
      <div className="max-w-md w-full space-y-4 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">kol.my Insight</h1>
        <p className="text-sm text-white/70">
          Linking your browser session to the Chrome extension. This page is safe to close once you see success.
        </p>
        {status === 'working' && (
          <p className="text-sm text-cyan-300" aria-live="polite">
            {message}
          </p>
        )}
        {status === 'ok' && (
          <>
            <p className="text-sm text-emerald-300" aria-live="polite">
              {message}
            </p>
            {expiresAt && (
              <p className="text-xs text-white/50">
                This device stays connected until{' '}
                {new Date(expiresAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
                . You can disconnect it any time from Extension devices.
              </p>
            )}
          </>
        )}
        {status === 'err' && (
          <p className="text-sm text-rose-300" aria-live="polite">
            {message}
          </p>
        )}
        <div className="pt-4 flex flex-col gap-2 text-sm">
          <Link to="/agency/extension-devices" className="text-cyan-400 hover:underline">
            Manage connected devices
          </Link>
          <Link to="/agency/dashboard" className="text-cyan-400 hover:underline">
            Go to agency dashboard
          </Link>
          <Link to="/login" className="text-white/50 hover:text-white/80 hover:underline">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
